import { useEffect, useState } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import validate from '../RegistrantContactInfoValidation';
import NavLinks from '../../../NavLinks';
import useOmrData from '../../../state/omr/UseOmrData';

import useForm from '@usas/ui-web/utils/UseForm';
import Constants, { COUNTRY_CODE_CANADA, COUNTRY_CODE_MEXICO, COUNTRY_CODE_UNITED_STATES } from '@usas/ui-web/utils/Constants';
import useDidMountEffect from '@usas/ui-web/utils/UseDidMountEffect';
import usePhoneTypeData from '@usas/ui-web/state/phoneType/UsePhoneTypeData';
import useLeftNavModalData from '@usas/ui-web/state/leftNavModal/UseLeftNavModalData';
import useCountryCombobox from '@usas/ui-web/components/comboboxes/countryCombobox/UseCountryCombobox';
import useStatesCombobox from '@usas/ui-web/components/comboboxes/statesCombobox/UseStatesCombobox';

const INITIAL_FORM_STATE = {
  email: '',
  phoneNumber: '',
  phoneTypeId: Constants.DEFAULT_ID,
  address1: '',
  address2: '',
  city: '',
  stateCode: '',
  stateName: '',
  countryCode: '',
  countryName: '',
  postalCode: '',
  isCountryUSA: false,
  isCountryMexico: false,
  isCountryCanada: false,
  validatedAddress: null,
  showedAddressSuggestion: false
};

const INITIAL_VALIDATE_STATE = {
  isValidating: false,
  validatedAddress: null
};

const useRegistrantContactInfoOther = () => {
  const navigate = useNavigate();
  const { personState, savePersonState, isSaved } = useOmrData();
  const { leftNavModalState, setLeftNavModalState } = useLeftNavModalData();
  const { phoneTypeState } = usePhoneTypeData();
  const [validateState, setValidateState] = useState(INITIAL_VALIDATE_STATE);
  const {
    formState,
    errorState,
    onFormValueChanged,
    onValueTextPairChanged,
    setFormData,
    setFormState,
    handleSubmit,
    updateFormState,
    setIsDirty
  } = useForm(INITIAL_FORM_STATE, submitFormCallback, validate);
  const { getStateNameById } = useStatesCombobox();
  const { countryData } = useCountryCombobox();

  const handleSubmitProxy = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setValidateState({
      isValidating: true
    });
    await handleSubmit();
    setValidateState({
      isValidating: false,
      validatedAddress: formState.validatedAddress
    });
    updateFormState('showedAddressSuggestion', true);
  };

  const onCountryChanged = (countryCode, countryName) => {
    let newFormState = {
      ...formState,
      countryCode,
      countryName,
      isCountryUSA: false,
      isCountryMexico: false,
      isCountryCanada: false,
      stateCode: '',
      stateName: ''
    };

    if (countryCode === COUNTRY_CODE_UNITED_STATES) {
      newFormState = {
        ...newFormState,
        isCountryUSA: true,
      };
    } else if (countryCode === COUNTRY_CODE_MEXICO) {
      newFormState = {
        ...newFormState,
        isCountryMexico: true,
      };
    } else if (countryCode === COUNTRY_CODE_CANADA) {
      newFormState = {
        ...newFormState,
        isCountryCanada: true,
      };
    }

    setFormState(newFormState);
    setIsDirty(true);
  }

  const onBackButtonClicked = (e) => {
    e?.preventDefault();
    document.getElementById('omrHeadingId')?.scrollIntoView();
    navigate(NavLinks.ABOUT_MEMBER);
  };

  function submitFormCallback(formState) {
    const newContactInfo = {
      emailAddress: formState.email?.trim(),
      phoneNumber: formState.phoneNumber?.trim(),
      phoneTypeId: formState.phoneTypeId,
      address: {
        address1: formState.address1?.trim(),
        address2: formState.address2?.trim(),
        city: formState.city?.trim(),
        stateCode: formState.stateCode,
        stateName: formState.stateName,
        postalCode: formState.postalCode?.trim(),
        countryCode: formState.countryCode
      }
    };

    savePersonState({
      ...personState,
      person: {
        ...personState.person,
        contactInfo: newContactInfo
      }
    });
  };

  useEffect(() => {
    if (personState && Object.keys(personState).length > 0) {
      if (personState.person?.contactInfo.emailAddress !== '' && personState.person?.contactInfo.phoneNumber !== '') {
        let formStateCopy = [];
        if (formState.email !== '' && isSaved === false) {
          formStateCopy = JSON.parse(JSON.stringify(formState));
          if ((formStateCopy.email !== personState.person?.contactInfo?.emailAddress) ||
            (formStateCopy.phoneNumber !== personState.person?.contactInfo?.phoneNumber) ||
            (formStateCopy.phoneTypeId !== personState.person?.contactInfo?.phoneTypeId) ||
            (formStateCopy.address1 !== personState.person?.contactInfo?.address?.address1) ||
            (formStateCopy.address2 !== personState.person?.contactInfo?.address?.address2) ||
            (formStateCopy.city !== personState.person?.contactInfo?.address?.city) ||
            (formStateCopy.stateCode !== personState.person?.contactInfo?.address?.stateCode) ||
            (formStateCopy.postalCode !== personState.person?.contactInfo?.address?.postalCode) ||
            (formStateCopy.countryCode !== personState.person?.contactInfo?.address?.countryCode)) {
            setLeftNavModalState({ ...leftNavModalState, formStateSaved: false });
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, personState, isSaved]);

  useEffect(() => {
    let formStateCopy = [];
    if (isSaved === false
      && (personState.person?.contactInfo.emailAddress?.trim() === '' && personState.person?.contactInfo.phoneNumber?.trim() === '')) {
      formStateCopy = JSON.parse(JSON.stringify(formState));
      if ((formStateCopy.email?.trim() === '') ||
        (formStateCopy.phoneNumber?.trim() === '') ||
        (formStateCopy.phoneTypeId === Constants.DEFAULT_ID) ||
        (formStateCopy.address1?.trim() === '') ||
        (formStateCopy.address2?.trim() === '') ||
        (formStateCopy.city?.trim() === '') ||
        (formStateCopy.stateCode === Constants.DEFAULT_ID) ||
        (formStateCopy.stateName === '') ||
        (formStateCopy.postalCode?.trim() === '') ||
        (formStateCopy.countryCode === '') ||
        (formStateCopy.countryName === '')) {
        setLeftNavModalState({ ...leftNavModalState, formStateSaved: false });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, personState.person, isSaved]);


  useEffect(() => {
    if (!formState.email && personState.person) {
      const contactInfo = personState.person?.contactInfo || {};

      const firstPhoneTypeId = Array.isArray(phoneTypeState.arrayData) && phoneTypeState.arrayData.length > 0
        ? phoneTypeState.arrayData[0].phoneTypeId
        : Constants.DEFAULT_ID;
      const phoneTypeId = contactInfo.phoneTypeId > 0 ? contactInfo.phoneTypeId : firstPhoneTypeId;

      setFormData({
        ...formState,
        email: contactInfo.emailAddress || '',
        phoneNumber: contactInfo.phoneNumber || '',
        phoneTypeId,
        address1: contactInfo.address?.address1 || '',
        address2: contactInfo.address?.address2 || '',
        city: contactInfo.address?.city || '',
        stateCode: contactInfo.address?.stateCode || '',
        stateName: contactInfo.address?.stateCode ? getStateNameById(contactInfo.address?.stateCode) : '',
        postalCode: contactInfo.address?.postalCode || '',
        countryCode: contactInfo.address?.countryCode || COUNTRY_CODE_UNITED_STATES,
        countryName: contactInfo.address?.countryCode ? countryData?.find(x => x.id === contactInfo.address?.countryCode)?.name : countryData?.find(x => x.id === COUNTRY_CODE_UNITED_STATES)?.name,
        isCountryUSA: contactInfo.address?.countryCode ? contactInfo.address?.countryCode === COUNTRY_CODE_UNITED_STATES : true,
        isCountryMexico: contactInfo.address?.countryCode === COUNTRY_CODE_MEXICO,
        isCountryCanada: contactInfo.address?.countryCode === COUNTRY_CODE_CANADA,
      }, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personState]);

  // do this after the save state completes
  useDidMountEffect(() => {
    if (isSaved === true) {
      document.getElementById('omrHeadingId')?.scrollIntoView();
      navigate(NavLinks.PRIMARY_CONTACT_INFO_O15_OTHER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaved]);

  return {
    formState,
    errorState,
    onFormValueChanged,
    onValueTextPairChanged,
    onCountryChanged,
    setFormData,
    onBackButtonClicked,
    onNextButtonClicked: handleSubmitProxy,
    validateState,
    isCountryUSA: formState.isCountryUSA === true,
    isCountryMexico: formState.isCountryMexico === true,
    isCountryCanada: formState.isCountryCanada === true
  };
};

export default useRegistrantContactInfoOther;